// Idag: the day itself — hero greeting, the expected-pattern strip for the
// next 14 days, the check-in (mood -> plan size, then three skippable steps:
// weight, sleep, appetite), the day's plan and the streak.
//
// The strip renders the care profile's expected windows only. They are
// placeholders to confirm with the care team, never predictions — hence the
// fixed line under the strip and the deliberate refusal to guess when the next
// infusion falls. Streaks pause, never break (invariant 7): a paused streak is
// shown as rest, never as failure.

import { getToday, markTaskDone, type CheckinPayload, type PlanSize, type Task, type Today } from "../api";
import { SERVER_URL, getToken } from "../config";
import { clear, el } from "../dom";
import { flushQueue, submitCheckin } from "../queue";
import { getLang, t } from "../strings";

import {
  getCalendarInfo,
  getProgram,
  getStats,
  postCalendarEvent,
  type CalendarInfo,
  type CalendarPhase,
  type CycleInfo,
  type ProgramItem,
} from "../api";

type CyclePosition = CycleInfo;

/** Latest known weight, for prefilling the check-in's weight step. */
function getLastWeight(): Promise<{ date: string; kg: number } | null> {
  return getStats().then((stats) => stats.weight.points.at(-1) ?? null);
}

function postInfusionDay(date: string): Promise<unknown> {
  return postCalendarEvent({ date, kind: "infusion", title: t("idag2.anchor.eventTitle") });
}

// Date and slot are computed on Stockholm wall time, matching server/src/core/time.ts —
// the server treats the client slot as advisory, but they must still tell the same story.
function stockholmParts(): { date: string; hour: number } {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    hour: Number(get("hour")) % 24,
  };
}

function todayDate(): string {
  return stockholmParts().date;
}

function currentSlot(): "morning" | "midday" | "evening" | "night" {
  const h = stockholmParts().hour;
  if (h >= 5 && h < 11) return "morning";
  if (h >= 11 && h < 17) return "midday";
  if (h >= 17 && h < 22) return "evening";
  return "night";
}

function greetingKey(): Parameters<typeof t>[0] {
  const h = stockholmParts().hour;
  if (h >= 5 && h < 11) return "idag2.greeting.morning";
  if (h >= 11 && h < 18) return "idag2.greeting.day";
  if (h >= 18 && h < 23) return "idag2.greeting.evening";
  return "idag2.greeting.night";
}

function locale(): string {
  return getLang() === "en" ? "en-GB" : "sv-SE";
}

// Date strings are calendar dates, so they are formatted at UTC noon — that
// keeps the printed weekday the same one the server counted.
function dateObj(date: string): Date {
  return new Date(`${date}T12:00:00Z`);
}

function addDays(date: string, days: number): string {
  return new Date(dateObj(date).getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

function fmtDate(date: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale(), { ...opts, timeZone: "UTC" }).format(dateObj(date));
}

function fmtNumber(value: number, decimals: number): string {
  return value.toLocaleString(locale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Window labels arrive lowercase from the profile; they start sentences here. */
function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const MOODS: Array<{ value: number; key: Parameters<typeof t>[0] }> = [
  { value: 5, key: "today.checkin.mood.5" },
  { value: 4, key: "today.checkin.mood.4" },
  { value: 3, key: "today.checkin.mood.3" },
  { value: 2, key: "today.checkin.mood.2" },
  { value: 1, key: "today.checkin.mood.1" },
];

const PLANS: Array<{ value: PlanSize; key: Parameters<typeof t>[0]; hint: Parameters<typeof t>[0] }> = [
  { value: "small", key: "today.checkin.plan.small", hint: "today.checkin.plan.small.hint" },
  { value: "medium", key: "today.checkin.plan.medium", hint: "today.checkin.plan.medium.hint" },
  { value: "large", key: "today.checkin.plan.large", hint: "today.checkin.plan.large.hint" },
  { value: "rest", key: "today.checkin.plan.rest", hint: "today.checkin.plan.rest.hint" },
];

const APPETITES: Array<{ value: number; key: Parameters<typeof t>[0] }> = [
  { value: 9, key: "idag2.appetite.good" },
  { value: 6, key: "idag2.appetite.ok" },
  { value: 3, key: "idag2.appetite.little" },
  { value: 0, key: "idag2.appetite.none" },
];

const STRIP_DAYS = 14;

// Windows we colour by name; anything else the profile lists still shows as a
// caution day carrying its own note, so a new window never renders as "plain".
const TOUGH_PHASES = new Set(["nausea"]);
const INFECTION_PHASES = new Set(["neutropenia"]);

type Fill = "tough" | "caution" | "good" | "neutral";

interface StripDay {
  date: string;
  cycleDay: number;
  windows: CalendarPhase[];
  fill: Fill;
  infection: boolean;
}

function buildStrip(cycle: CyclePosition, phases: CalendarPhase[]): StripDay[] {
  const today = todayDate();
  const days: StripDay[] = [];
  for (let i = 0; i < STRIP_DAYS; i += 1) {
    const cycleDay = cycle.day + i;
    // Past the expected cycle length we know nothing: the next infusion is not
    // in the calendar, and guessing when it falls would be an invented number.
    const beyond = cycle.ofDays !== null && cycleDay > cycle.ofDays;
    const windows = beyond ? [] : phases.filter((p) => cycleDay >= p.from && cycleDay <= p.to);
    const infection = windows.some((p) => INFECTION_PHASES.has(p.id));
    const named = windows.filter((p) => !INFECTION_PHASES.has(p.id));
    let fill: Fill;
    if (named.some((p) => TOUGH_PHASES.has(p.id))) fill = "tough";
    else if (named.length > 0) fill = "caution";
    else if (beyond || infection || phases.length === 0) fill = "neutral";
    else fill = "good";
    days.push({ date: addDays(today, i), cycleDay, windows, fill, infection });
  }
  return days;
}

type FieldRead = { ok: true; value: number } | { ok: false; empty: boolean };

interface NumberField {
  node: HTMLElement;
  read(): FieldRead;
}

// Text input rather than type=number: a Swedish keypad types a comma, which a
// number input silently throws away. The minus/plus buttons do the small steps
// so the value can be set without opening a keyboard at all.
function numberField(opts: {
  value: number | null;
  step: number;
  min: number;
  max: number;
  decimals: number;
  unit: string;
  label: string;
}): NumberField {
  const input = el("input", {
    type: "text",
    inputmode: "decimal",
    autocomplete: "off",
    class: "num-input",
    "aria-label": opts.label,
    value: opts.value === null ? "" : fmtNumber(opts.value, opts.decimals),
  });

  const read = (): FieldRead => {
    const raw = input.value.trim().replace(",", ".");
    if (raw === "") return { ok: false, empty: true };
    const n = Number(raw);
    if (!Number.isFinite(n) || n < opts.min || n > opts.max) return { ok: false, empty: false };
    return { ok: true, value: Math.round(n * 10) / 10 };
  };

  const nudge = (delta: number): void => {
    const current = read();
    const base = current.ok ? current.value : (opts.value ?? opts.min);
    const next = Math.min(opts.max, Math.max(opts.min, Math.round((base + delta) * 10) / 10));
    input.value = fmtNumber(next, opts.decimals);
  };

  const minus = el("button", { class: "btn step", type: "button", "aria-label": "−", text: "−" });
  const plus = el("button", { class: "btn step", type: "button", "aria-label": "+", text: "+" });
  minus.addEventListener("click", () => nudge(-opts.step));
  plus.addEventListener("click", () => nudge(opts.step));

  const node = el("div", { class: "num-field" }, [
    minus,
    input,
    el("span", { class: "num-unit", text: opts.unit }),
    plus,
  ]);
  return { node, read };
}

export function mountIdag(root: HTMLElement): () => void {
  let disposed = false;

  type Step = "mood" | "plan" | "weight" | "sleep" | "appetite" | "done";

  let today: Today | null = null;
  let calendar: CalendarInfo | null = null;
  let lastWeightKg: number | null = null;
  // The program menu (when/why/how per item) and which rows stand open —
  // expansion survives the rerender that follows a tick.
  let program = new Map<string, ProgramItem>();
  const openRows = new Set<string>();
  let step: Step = "mood";
  let selectedDay = 0;
  let banner: { text: string; kind: "ok" | "err" } | null = null;
  // date/slot are captured with the first write so the optional answers land on
  // the same check-in even if the clock crosses a slot boundary meanwhile.
  const draft: {
    date: string;
    slot: CheckinPayload["slot"];
    mood: number | null;
    plan: PlanSize | null;
    weight_kg: number | null;
    sleep_hours: number | null;
    appetite: number | null;
  } = {
    date: todayDate(),
    slot: currentSlot(),
    mood: null,
    plan: null,
    weight_kg: null,
    sleep_hours: null,
    appetite: null,
  };

  function hasCheckin(day: Today): boolean {
    return Boolean(day.checkin && day.checkin.mood && day.checkin.plan_size);
  }

  async function load(): Promise<void> {
    clear(root);
    root.append(hero(), el("p", { class: "muted", text: t("today.loading") }));

    if (!getToken()) {
      clear(root);
      root.append(hero(), el("div", { class: "notice", text: t("idag2.needLogin") }));
      return;
    }

    try {
      await flushQueue(); // queued check-ins first, so the fetched day is current
      const [day, cal, weight, menu] = await Promise.all([
        getToday(),
        getCalendarInfo().catch(() => null),
        getLastWeight().catch(() => null),
        getProgram().catch(() => null),
      ]);
      if (disposed) return;
      today = day;
      calendar = cal;
      lastWeightKg = weight?.kg ?? null;
      program = new Map((menu?.items ?? []).map((item) => [item.id, item]));
      step = hasCheckin(day) ? "done" : "mood";
      render();
    } catch {
      if (disposed) return;
      clear(root);
      const retry = el("button", { class: "btn", type: "button", text: t("today.retry") });
      retry.addEventListener("click", () => void load());
      root.append(hero(), el("div", { class: "notice err", text: t("today.error") }), retry);
    }
  }

  // ── 1. hero ───────────────────────────────────────────────────────────────
  function hero(): HTMLElement {
    const date = todayDate();
    const node = el("header", { class: "hero" }, [
      el("h1", { text: t(greetingKey()) }),
      el("p", { class: "date", text: fmtDate(date, { weekday: "long", day: "numeric", month: "long" }) }),
    ]);
    const cycle = calendar?.cycle;
    if (cycle) {
      const active = (calendar?.phases ?? []).filter(
        (p) => cycle.day >= p.from && cycle.day <= p.to
      );
      const dayText =
        cycle.ofDays === null
          ? `${t("idag2.cycle.day")} ${cycle.day}`
          : `${t("idag2.cycle.day")} ${cycle.day} ${t("idag2.cycle.of")} ${cycle.ofDays}`;
      const labels = active.map((p) => p.label_sv).join(", ");
      // "Dag 9 av 21 · illamåendetopp" — the server's own words, nothing added.
      node.append(el("p", { class: "cycle", text: labels ? `${dayText} · ${labels}` : dayText }));
    }
    return node;
  }

  // ── 2. the 14-day strip ───────────────────────────────────────────────────
  // Null when the calendar could not be fetched: "no treatment day entered" is
  // a claim we may only make when the server actually answered.
  function bandSection(): HTMLElement | null {
    if (!calendar) return null;
    const cycle = calendar.cycle;
    if (!cycle) return anchorCard();

    const card = el("div", { class: "card" });
    card.append(
      el("h2", { text: t("idag2.band.title") }),
      el("p", { class: "muted small", text: t("idag2.band.hint") })
    );

    const days = buildStrip(cycle, calendar?.phases ?? []);
    const strip = el("div", { class: "band" });
    days.forEach((day, index) => {
      const chip = el("button", {
        class: `chip ${day.fill}${day.infection ? " inf" : ""}${index === 0 ? " today" : ""}${
          index === selectedDay ? " sel" : ""
        }`,
        type: "button",
        "aria-pressed": index === selectedDay ? "true" : "false",
        "aria-label": chipLabel(day),
      });
      chip.append(
        el("span", { class: "wd", text: fmtDate(day.date, { weekday: "short" }) }),
        el("span", { class: "dnum", text: fmtDate(day.date, { day: "numeric" }) })
      );
      if (day.infection) chip.append(el("span", { class: "mark", "aria-hidden": "true" }));
      chip.addEventListener("click", () => {
        selectedDay = index;
        render();
      });
      strip.append(chip);
    });
    card.append(strip);
    const tuned = (calendar?.phases ?? []).some((p) => p.adjusted === true);
    card.append(
      el("p", {
        class: "muted small band-note",
        text: tuned ? t("idag2.band.adjusted") : t("idag2.band.note"),
      })
    );

    const picked = days[selectedDay] ?? days[0];
    if (picked) card.append(dayDetail(picked));
    if (cycle.ofDays !== null && cycle.day + STRIP_DAYS - 1 > cycle.ofDays) {
      card.append(el("p", { class: "muted small", text: t("idag2.band.noNext") }));
    }
    return card;
  }

  function chipLabel(day: StripDay): string {
    const parts = [
      fmtDate(day.date, { weekday: "long", day: "numeric", month: "long" }),
      `${t("idag2.cycle.day")} ${day.cycleDay}`,
      ...day.windows.map((w) => w.label_sv),
    ];
    if (day.infection) parts.push(t("idag2.band.infection"));
    return parts.join(", ");
  }

  function dayDetail(day: StripDay): HTMLElement {
    const box = el("div", { class: "day-detail" }, [
      el("h3", {
        text: `${fmtDate(day.date, { weekday: "long", day: "numeric", month: "long" })} · ${t(
          "idag2.cycle.day"
        )} ${day.cycleDay}`,
      }),
    ]);
    if (day.windows.length === 0) {
      box.append(el("p", { class: "muted", text: t("idag2.band.calm") }));
      return box;
    }
    for (const win of day.windows) {
      // An adjusted window shows its provenance: his days moved it, and from
      // where — the feedback loop must be visible to keep feeding it.
      const adjusted =
        win.adjusted === true && win.base_from !== undefined && win.base_to !== undefined
          ? ` ${t("idag2.band.tunedWin")
              .replace("%FROM%", String(win.from))
              .replace("%TO%", String(win.to))
              .replace("%BFROM%", String(win.base_from))
              .replace("%BTO%", String(win.base_to))}`
          : "";
      box.append(
        el("p", { class: "win" }, [
          el("strong", { text: `${capitalize(win.label_sv)}: ` }),
          document.createTextNode(win.note_sv + adjusted),
        ])
      );
    }
    if (day.infection) {
      box.append(
        el("p", { class: "muted small" }, [
          el("span", { class: "mark inline", "aria-hidden": "true" }),
          document.createTextNode(t("idag2.band.infection")),
        ])
      );
    }
    return box;
  }

  // The app must be able to anchor itself here too: without an infusion day
  // there is no cycle, and guessing one would be an invented number.
  function anchorCard(): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(
      el("h2", { text: t("idag2.anchor.title") }),
      el("p", { class: "muted", text: t("idag2.anchor.body") })
    );
    const field = el("div", { class: "field" });
    const input = el("input", { type: "date", id: "anchor-date", max: todayDate(), value: todayDate() });
    field.append(el("label", { for: "anchor-date", text: t("idag2.anchor.label") }), input);
    const save = el("button", { class: "btn primary", type: "button", text: t("idag2.anchor.save") });
    save.addEventListener("click", () => {
      const date = input.value;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
      save.setAttribute("disabled", "true");
      void postInfusionDay(date)
        .then(() => {
          if (disposed) return;
          banner = { text: t("idag2.anchor.saved"), kind: "ok" };
          return load();
        })
        .catch(() => {
          if (disposed) return;
          save.removeAttribute("disabled");
          card.append(el("div", { class: "notice err", text: t("idag2.anchor.error") }));
        });
    });
    card.append(field, save);
    return card;
  }

  // ── 3. check-in ───────────────────────────────────────────────────────────
  function moodCard(): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("today.checkin.mood.title") }));
    const grid = el("div", { class: "choice-grid" });
    for (const mood of MOODS) {
      const btn = el("button", { class: "btn word", type: "button", text: t(mood.key) });
      btn.addEventListener("click", () => {
        draft.mood = mood.value;
        step = "plan";
        render();
      });
      grid.append(btn);
    }
    card.append(grid);
    return card;
  }

  function planCard(): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("today.checkin.plan.title") }));
    const grid = el("div", { class: "choice-grid" });
    for (const plan of PLANS) {
      const btn = el("button", { class: "btn", type: "button" }, [
        el("span", {}, [
          el("strong", { text: t(plan.key) }),
          el("br"),
          el("span", { class: "muted small", text: t(plan.hint) }),
        ]),
      ]);
      btn.addEventListener("click", () => void submitCore(plan.value));
      grid.append(btn);
    }
    const back = el("button", { class: "btn quiet", type: "button", text: t("today.checkin.back") });
    back.addEventListener("click", () => {
      step = "mood";
      render();
    });
    card.append(grid, back);
    return card;
  }

  function measureCard(kind: "weight" | "sleep"): HTMLElement {
    const isWeight = kind === "weight";
    const card = el("div", { class: "card" });
    card.append(
      el("h2", { text: t(isWeight ? "idag2.weight.title" : "idag2.sleep.title") }),
      el("p", { class: "muted", text: t(isWeight ? "idag2.weight.hint" : "idag2.sleep.hint") })
    );
    const field = numberField(
      isWeight
        ? {
            value: lastWeightKg,
            step: 0.1,
            min: 30,
            max: 200,
            decimals: 1,
            unit: t("idag2.weight.unit"),
            label: t("idag2.weight.title"),
          }
        : {
            value: null,
            step: 0.5,
            min: 0,
            max: 20,
            decimals: 1,
            unit: t("idag2.sleep.unit"),
            label: t("idag2.sleep.title"),
          }
    );
    const hint = el("p", { class: "muted small", text: t("idag2.num.invalid") });
    const save = el("button", { class: "btn primary", type: "button", text: t("idag2.checkin.save") });
    const skip = el("button", { class: "btn quiet", type: "button", text: t("idag2.checkin.skip") });
    save.addEventListener("click", () => {
      const value = field.read();
      if (!value.ok && !value.empty) {
        if (!hint.isConnected) field.node.after(hint);
        return;
      }
      if (value.ok) {
        if (isWeight) draft.weight_kg = value.value;
        else draft.sleep_hours = value.value;
      }
      step = isWeight ? "sleep" : "appetite";
      render();
    });
    skip.addEventListener("click", () => {
      step = isWeight ? "sleep" : "appetite";
      render();
    });
    card.append(field.node, el("div", { class: "row" }, [save, skip]));
    return card;
  }

  function appetiteCard(): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("idag2.appetite.title") }));
    const grid = el("div", { class: "choice-grid" });
    for (const item of APPETITES) {
      const btn = el("button", { class: "btn word", type: "button", text: t(item.key) });
      btn.addEventListener("click", () => {
        draft.appetite = item.value;
        void submitExtras();
      });
      grid.append(btn);
    }
    const skip = el("button", { class: "btn quiet", type: "button", text: t("idag2.checkin.skip") });
    skip.addEventListener("click", () => void submitExtras());
    card.append(grid, skip);
    return card;
  }

  // Mood + plan size go in first so the planner can build the day; the three
  // optional answers follow as a second write on the same date and slot, which
  // the server merges into the same check-in.
  async function submitCore(plan: PlanSize): Promise<void> {
    const mood = draft.mood ?? 3;
    draft.plan = plan;
    draft.date = todayDate();
    draft.slot = currentSlot();
    const payload: CheckinPayload = {
      client_uuid: crypto.randomUUID(),
      date: draft.date,
      slot: draft.slot,
      mood,
      plan_size: plan,
      source: "pwa",
    };
    const result = await submitCheckin(payload);
    if (disposed) return;

    if (result === "queued") {
      if (today) {
        today = { ...today, checkin: { date: draft.date, slot: draft.slot, mood, plan_size: plan } };
      }
      banner = { text: t("today.offline.saved"), kind: "ok" };
      step = "weight";
      render();
      return;
    }

    const fresh = await getToday().catch(() => null);
    if (disposed) return;
    if (fresh) today = fresh;
    if (result === "sent") {
      banner = { text: t("today.checkin.done"), kind: "ok" };
      step = "weight";
    } else if (fresh && hasCheckin(fresh)) {
      // The server already holds today's check-in — the goal state stands.
      banner = null;
      step = "weight";
    } else {
      banner = { text: t("today.checkin.rejected"), kind: "err" };
      step = "mood";
    }
    render();
  }

  async function submitExtras(): Promise<void> {
    const extras: { weight_kg?: number; sleep_hours?: number; appetite?: number } = {};
    if (draft.weight_kg !== null) extras.weight_kg = draft.weight_kg;
    if (draft.sleep_hours !== null) extras.sleep_hours = draft.sleep_hours;
    if (draft.appetite !== null) extras.appetite = draft.appetite;
    step = "done";
    if (Object.keys(extras).length === 0) {
      render();
      return;
    }
    const payload: CheckinPayload & typeof extras = {
      client_uuid: crypto.randomUUID(),
      date: draft.date,
      slot: draft.slot,
      mood: draft.mood ?? today?.checkin?.mood ?? 3,
      plan_size: draft.plan ?? today?.checkin?.plan_size ?? "small",
      source: "pwa",
      ...extras,
    };
    const result = await submitCheckin(payload);
    if (disposed) return;
    if (result === "rejected") {
      banner = { text: t("today.checkin.rejected"), kind: "err" };
    } else {
      banner = {
        text: result === "queued" ? t("today.offline.saved") : t("idag2.checkin.saved"),
        kind: "ok",
      };
      if (draft.weight_kg !== null) lastWeightKg = draft.weight_kg;
    }
    render();
  }

  // ── 4. the day's plan ─────────────────────────────────────────────────────
  function tasksCard(day: Today): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("idag2.plan.title") }));
    const tasks = day.tasks ?? [];
    if (tasks.length === 0) {
      const empty =
        day.checkin?.plan_size === "rest" ? t("today.tasks.rest") : t("idag2.plan.empty");
      card.append(el("p", { class: "muted", text: empty }));
      return card;
    }
    const list = el("div", { class: "stack" });
    for (const task of tasks) list.append(taskRow(task));
    card.append(list);
    return card;
  }

  function completeTask(task: Task, node: HTMLElement): void {
    if (task.done) return; // no undo in the MVP; keeps the flow forgiving
    task.done = true;
    node.classList.add("done");
    void markTaskDone(task.id)
      .then(() => refreshToday())
      .catch(() => {
        // Network failed — keep the optimistic tick; the server reconciles
        // on the next successful /api/today load.
      });
  }

  /** Rows without program detail (old rows, trimmed menus) stay one-tap. */
  function plainTaskRow(task: Task): HTMLElement {
    const row = el("button", { class: task.done ? "task done" : "task", type: "button" }, [
      el("span", { class: "box", text: "✓" }),
      el("span", { class: "title", text: task.title }),
      el("span", {
        class: "doneword",
        text: task.done ? t("idag2.plan.isDone") : t("idag2.plan.markDone"),
      }),
    ]);
    row.addEventListener("click", () => completeTask(task, row));
    return row;
  }

  // A program task opens: when at the top, then why, then how — the plan is
  // inspectable, not just tickable. The checkbox on the left stays one-tap.
  function taskRow(task: Task): HTMLElement {
    const info = task.ref ? program.get(task.ref) : undefined;
    if (!info || (!info.why_sv && !info.how_sv)) return plainTaskRow(task);

    const wrap = el("div", { class: task.done ? "prog done" : "prog" });
    const row = el("div", { class: "prog-row" });
    const check = el(
      "button",
      {
        class: "prog-check",
        type: "button",
        "aria-label": task.done ? t("idag2.plan.isDone") : t("idag2.plan.markDone"),
      },
      [el("span", { class: "box", text: "✓" })]
    );
    const expanded = openRows.has(task.id);
    const open = el(
      "button",
      { class: "prog-open", type: "button", "aria-expanded": expanded ? "true" : "false" },
      [
        el("span", { class: "title", text: task.title }),
        el("span", { class: "chev", "aria-hidden": "true", text: "▾" }),
      ]
    );
    row.append(check, open);

    const detail = el("div", { class: "prog-detail" });
    if (info.when_sv) detail.append(el("p", { class: "prog-when", text: info.when_sv }));
    if (info.why_sv) {
      detail.append(
        el("p", { class: "prog-label", text: t("idag2.detail.why") }),
        el("p", { class: "prog-why", text: info.why_sv })
      );
    }
    if (info.how_sv) {
      detail.append(
        el("p", { class: "prog-label", text: t("idag2.detail.how") }),
        el("p", { class: "prog-how", text: info.how_sv })
      );
    }
    if (info.category === "andning") {
      const john = el("button", { class: "btn quiet", type: "button", text: t("idag2.detail.john") });
      john.addEventListener("click", () => {
        sessionStorage.setItem("phoenix.persona.next", "john");
        location.hash = "#/prata";
      });
      detail.append(john);
    }
    if (!task.done) {
      const doneBtn = el("button", {
        class: "btn primary",
        type: "button",
        text: t("idag2.plan.markDone"),
      });
      doneBtn.addEventListener("click", () => completeTask(task, wrap));
      detail.append(doneBtn);
    }
    detail.hidden = !expanded;

    open.addEventListener("click", () => {
      const show = detail.hidden;
      detail.hidden = !show;
      open.setAttribute("aria-expanded", show ? "true" : "false");
      if (show) openRows.add(task.id);
      else openRows.delete(task.id);
    });
    check.addEventListener("click", () => completeTask(task, wrap));

    wrap.append(row, detail);
    return wrap;
  }

  async function refreshToday(): Promise<void> {
    const fresh = await getToday().catch(() => null);
    if (disposed || !fresh) return;
    today = fresh;
    render();
  }

  // ── 5. streak + weekly letter ─────────────────────────────────────────────
  function streakCard(day: Today): HTMLElement {
    const streak = day.streak ?? 0;
    const card = el("div", { class: "card" });
    card.append(
      el("p", {
        class: "streak",
        text: streak === 1 ? t("idag2.streak.one") : `${streak} ${t("idag2.streak.many")}`,
      })
    );
    if (day.streak_paused) {
      card.append(el("p", { class: "muted", text: t("today.streak.paused") }));
    }
    return card;
  }

  function briefCard(): HTMLElement {
    const card = el("div", { class: "card" });
    const link = el("a", { class: "btn primary", href: "#/resa", text: t("idag2.brief.open") });
    card.append(el("h2", { text: t("idag2.brief.here") }), link);
    return card;
  }

  function render(): void {
    if (disposed) return;
    clear(root);
    root.append(hero());
    if (banner) {
      root.append(el("div", { class: banner.kind === "err" ? "notice err" : "notice ok", text: banner.text }));
    }
    // The day's own question comes first; the two-week outlook reads below it.
    if (step === "mood") root.append(moodCard());
    else if (step === "plan") root.append(planCard());
    else if (step === "weight" || step === "sleep") root.append(measureCard(step));
    else if (step === "appetite") root.append(appetiteCard());

    const day = today;
    if (day && hasCheckin(day)) root.append(tasksCard(day));

    const band = bandSection();
    if (band) root.append(band);

    if (day) {
      root.append(streakCard(day));
      if (day.brief_available) root.append(briefCard());
    }
  }

  void load();
  return () => {
    disposed = true;
  };
}
